import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Star,
  Calendar,
  MessageCircle,
  Video,
  Users,
  Award,
  Search,
  ChevronDown,
  Sparkles,
  Clock,
  TrendingUp,
  X,
  CheckCircle,
  Shield,
  Zap,
  Globe,
  BadgeCheck,
  Loader,
  Linkedin,
  Twitter,
} from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { mentorshipApi, showError } from "../lib";
import { useAuth } from "../context/AuthContext";
import SEO from "../components/SEO";

gsap.registerPlugin(ScrollTrigger);

// ─── Types ────────────────────────────────────────────────────────────────────

interface Mentor {
  _id: string;
  user: string;
  firstName?: string;
  lastName?: string;
  name: string;
  title: string;
  role: string;
  company: string;
  companyColor: string;
  bio: string;
  expertise: string[];
  pricePerSession: number;
  rating?: number;
  reviewsCount?: number;
  totalSessions: number;
  totalStudents: number;
  studentsCount?: number;
  sessionsCount?: number;
  reviews: number;
  students: number;
  sessions: number;
  price: number;
  isVerified: boolean;
  isTopMentor: boolean;
  verified?: boolean;
  topMentor?: boolean;
  avatar?: string;
  image?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  hourlyRate?: number;
  yearsOfExperience?: number;
  languages?: string[];
  availability?: string;
  responseTime?: string;
  availability_schedule?: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  }[];
  id?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtXAF = (xAF: number) =>
  new Intl.NumberFormat("fr-CM", {
    style: "currency",
    currency: "XAF",
    maximumFractionDigits: 0,
  }).format(xAF);

const RatingStars = ({
  rating,
  size = "sm",
}: {
  rating: number;
  size?: "sm" | "md";
}) => {
  const cls = size === "md" ? "w-3.5 h-3.5" : "w-2.5 h-2.5";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`${cls} ${s <= Math.round(rating) ? "text-amber-400 fill-amber-400" : "text-gray-300 fill-gray-300"}`}
        />
      ))}
    </div>
  );
};

// ─── Data ─────────────────────────────────────────────────────────────────────

const MENTORS: Mentor[] = [
  {
    id: 1,
    name: "Bongnteh Romarick",
    role: "Senior Tech Lead",
    company: "Google",
    companyColor: "bg-blue-500",
    image: "romarick.jpeg",
    expertise: [
      "React",
      "Node.js",
      "System Design",
      "Career Growth",
      "TypeScript",
    ],
    rating: 4.9,
    reviews: 128,
    students: 234,
    sessions: 567,
    price: 120,
    bio: "10+ years in tech, led teams at Google and Meta. Passionate about helping developers grow their careers and break into top-tier companies.",
    availability: "Mon, Wed, Fri",
    responseTime: "< 2 hrs",
    languages: ["English", "French"],
    verified: true,
    topMentor: true,
  },
  {
    id: 2,
    name: "James Anderson",
    role: "Business Strategist",
    company: "McKinsey",
    companyColor: "bg-slate-700",
    image: "mentor-1.jpg",
    expertise: [
      "Leadership",
      "Strategy",
      "Growth",
      "Consulting",
      "Executive Coaching",
    ],
    rating: 5.0,
    reviews: 89,
    students: 189,
    sessions: 423,
    price: 150,
    bio: "Former McKinsey consultant helping professionals navigate career transitions, leadership challenges, and entrepreneurial journeys.",
    availability: "Tue, Thu",
    responseTime: "< 4 hrs",
    languages: ["English"],
    verified: true,
    topMentor: true,
  },
  {
    id: 3,
    name: "Emily Chen",
    role: "Creative Director",
    company: "Apple",
    companyColor: "bg-gray-800",
    image: "mentor-2.jpg",
    expertise: [
      "UI/UX",
      "Branding",
      "Motion Design",
      "Portfolio Review",
      "Design Systems",
    ],
    rating: 4.8,
    reviews: 156,
    students: 312,
    sessions: 678,
    price: 100,
    bio: "Award-winning designer with 8 years at Apple. Helps designers build portfolios that stand out and land roles at top companies.",
    availability: "Mon–Fri",
    responseTime: "< 1 hr",
    languages: ["English", "Mandarin"],
    verified: true,
  },
  {
    id: 4,
    name: "Michael Torres",
    role: "Principal Engineer",
    company: "Netflix",
    companyColor: "bg-red-600",
    image: "mentor-3.jpg",
    expertise: ["Python", "AWS", "DevOps", "Microservices", "Kubernetes"],
    rating: 4.9,
    reviews: 201,
    students: 278,
    sessions: 534,
    price: 130,
    bio: "Built systems serving 200M+ users at Netflix. Specializes in scalable architecture, cloud solutions, and senior engineering interviews.",
    availability: "Weekends",
    responseTime: "< 6 hrs",
    languages: ["English", "Spanish"],
    verified: true,
    topMentor: true,
  },
  {
    id: 5,
    name: "Lisa Wong",
    role: "Senior PM",
    company: "Spotify",
    companyColor: "bg-emerald-600",
    image: "mentor-4.jpg",
    expertise: [
      "Product Strategy",
      "Agile",
      "User Research",
      "Roadmapping",
      "OKRs",
    ],
    rating: 4.7,
    reviews: 94,
    students: 167,
    sessions: 389,
    price: 110,
    bio: "Product leader at Spotify with experience launching features used by millions. Specializes in PM career transitions and interviews.",
    availability: "Mon, Wed, Fri",
    responseTime: "< 3 hrs",
    languages: ["English"],
    verified: true,
  },
  {
    id: 6,
    name: "David Park",
    role: "ML Engineering Lead",
    company: "Amazon",
    companyColor: "bg-orange-500",
    image: "testimonial-1.jpg",
    expertise: [
      "Machine Learning",
      "Python",
      "SQL",
      "Data Visualization",
      "NLP",
    ],
    rating: 4.8,
    reviews: 143,
    students: 245,
    sessions: 512,
    price: 140,
    bio: "PhD in CS, leading ML teams at Amazon. Helps aspiring data scientists break into the field and prepare for ML engineer interviews.",
    availability: "Tue, Thu, Sat",
    responseTime: "< 2 hrs",
    languages: ["English", "Korean"],
    verified: true,
  },
  {
    id: 7,
    name: "Nina Patel",
    role: "Staff Engineer",
    company: "Stripe",
    companyColor: "bg-violet-600",
    image: "testimonial-3.jpg",
    expertise: ["Fintech", "API Design", "Go", "Security", "Technical Writing"],
    rating: 4.9,
    reviews: 77,
    students: 134,
    sessions: 290,
    price: 160,
    bio: "Staff engineer at Stripe building payment infrastructure. Passionate about API design, technical writing, and engineering excellence.",
    availability: "Wed, Fri",
    responseTime: "< 4 hrs",
    languages: ["English", "Hindi"],
    verified: true,
    topMentor: true,
  },
  {
    id: 8,
    name: "Carlos Rivera",
    role: "Growth Director",
    company: "Airbnb",
    companyColor: "bg-rose-500",
    image: "testimonial-2.jpg",
    expertise: [
      "Growth Hacking",
      "Marketing Analytics",
      "A/B Testing",
      "SEO",
      "CRO",
    ],
    rating: 4.6,
    reviews: 112,
    students: 198,
    sessions: 445,
    price: 95,
    bio: "Led growth at Airbnb from 5M to 50M hosts. Expert in data-driven growth, experimentation, and building growth teams from scratch.",
    availability: "Mon–Thu",
    responseTime: "< 5 hrs",
    languages: ["English", "Spanish"],
    verified: true,
  },
  {
    id: 9,
    name: "Priya Sharma",
    role: "Design Lead",
    company: "Figma",
    companyColor: "bg-fuchsia-600",
    image: "testimonial-1.jpg",
    expertise: [
      "Product Design",
      "Figma",
      "Design Tokens",
      "Accessibility",
      "User Testing",
    ],
    rating: 4.8,
    reviews: 98,
    students: 212,
    sessions: 360,
    price: 105,
    bio: "Design lead at Figma — the tool designers live in. Helps designers level up from junior to senior and build systems that scale.",
    availability: "Mon, Tue, Thu",
    responseTime: "< 2 hrs",
    languages: ["English", "Hindi"],
    verified: true,
  },
];

const EXPERTISE_AREAS = [
  "All",
  "React",
  "Node.js",
  "Python",
  "UI/UX",
  "Leadership",
  "Strategy",
  "Machine Learning",
  "Product Strategy",
  "DevOps",
  "Figma",
];

const SORT_OPTIONS = [
  { value: "popular", label: "Most Popular" },
  { value: "rating", label: "Highest Rated" },
  { value: "price-lo", label: "Price: Low → High" },
  { value: "price-hi", label: "Price: High → Low" },
  { value: "sessions", label: "Most Sessions" },
];

// ─── Mentor Card ──────────────────────────────────────────────────────────────
// KEY FIX: Image is fully visible (no dark overlay on face area).
// Badges float cleanly on top with subtle backdrop-blur pills.
// Name/role sit below the image in the card body — not overlaid on the photo.

const MentorCard = ({
  mentor,
  onSelect,
}: {
  mentor: Mentor;
  onSelect: () => void;
}) => (
  <div className="flex flex-col overflow-hidden transition-all duration-300 bg-white border border-gray-100 shadow-sm mentor-card group rounded-2xl hover:shadow-xl hover:-translate-y-1">
    {/* ── Image block: clean, no obscuring gradient ── */}
    <div
      className="relative overflow-hidden bg-gray-100 shrink-0"
      style={{ height: "220px" }}
    >
      <img
        src={mentor.image || mentor.avatar || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=300&fit=crop"}
        alt={mentor.name}
        className="object-cover object-top w-full h-full transition-transform duration-500 group-hover:scale-105"
      />

      {/* Top-left: company + top mentor badges */}
      <div className="absolute top-3 left-3 flex flex-col gap-1.5">
        <span
          className={`text-[9px] font-black uppercase tracking-widest text-white px-2 py-0.5 rounded-md shadow-md ${mentor.companyColor || "bg-gray-800"}`}
        >
          {mentor.company}
        </span>
        {(mentor.topMentor || mentor.isTopMentor) && (
          <span className="text-[9px] font-bold uppercase tracking-wide text-amber-900 bg-amber-400 px-2 py-0.5 rounded-md flex items-center gap-1 shadow-md">
            <Star className="w-2.5 h-2.5 fill-amber-900" /> Top Mentor
          </span>
        )}
      </div>

      {/* Top-right: rating pill */}
      <div className="absolute flex items-center gap-1 px-2 py-1 border rounded-full shadow-md top-3 right-3 bg-white/90 backdrop-blur-sm border-white/60">
        <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
        <span className="text-gray-800 text-[10px] font-bold">
          {mentor.rating?.toFixed(1) || "New"}
        </span>
      </div>

      {/* Bottom-right: verified badge */}
      {(mentor.verified || mentor.isVerified) && (
        <div className="absolute flex items-center justify-center bg-white rounded-full shadow-md bottom-3 right-3 w-7 h-7">
          <BadgeCheck className="w-4 h-4 text-blue-500" />
        </div>
      )}
    </div>

    {/* ── Card Body ── */}
    <div className="flex flex-col flex-1 p-4">
      {/* Name + role */}
      <div className="mb-2">
        <h3 className="text-sm font-bold leading-tight text-gray-900">
          {mentor.name}
        </h3>
        <p className="text-[11px] text-gray-500 mt-0.5">{mentor.title || mentor.specialty}</p>
      </div>

      <p className="mb-3 text-xs leading-relaxed text-gray-500 line-clamp-2">
        {mentor.bio}
      </p>

      {/* Skills */}
      <div className="flex flex-wrap gap-1 mb-3">
        {mentor.expertise.slice(0, 3).map((s) => (
          <span
            key={s}
            className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
          >
            {s}
          </span>
        ))}
        {mentor.expertise.length > 3 && (
          <span className="text-[10px] font-semibold bg-gray-50 text-gray-400 px-2 py-0.5 rounded-full border border-gray-200">
            +{mentor.expertise.length - 3}
          </span>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3 text-[10px] text-gray-400 mb-3 pb-3 border-b border-gray-100">
        <span className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          {mentor.studentsCount || mentor.students || mentor.totalStudents || 0} students
        </span>
        <span className="flex items-center gap-1">
          <Video className="w-3 h-3" />
          {mentor.sessionsCount || mentor.sessions || mentor.totalSessions || 0} sessions
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {mentor.responseTime || "< 2 hrs"}
        </span>
      </div>

      {/* Availability + languages */}
      <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-3">
        <Calendar className="w-3 h-3 shrink-0" />
        <span className="truncate">{mentor.availability}</span>
        <span className="text-gray-300">·</span>
        <Globe className="w-3 h-3 shrink-0" />
        <span className="truncate">{mentor.languages.join(", ")}</span>
      </div>

      {/* Price + CTA */}
      <div className="flex items-center justify-between mt-auto">
        <div>
          <p className="text-sm font-black text-gray-900">
            {fmtXAF(mentor.pricePerSession || mentor.price)}
          </p>
          <p className="text-[9px] text-gray-400 uppercase tracking-wide">
            per session
          </p>
        </div>
        <button
          onClick={onSelect}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-gray-900 hover:bg-blue-600 text-white text-[11px] font-bold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20 group/btn"
        >
          Book Now
          <ArrowRight className="w-3 h-3 transition-transform group-hover/btn:translate-x-0.5" />
        </button>
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const MentorshipPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedExpertise, setExpert] = useState("All");
  const [sortBy, setSortBy] = useState("popular");
  const [selectedMentor, setSelected] = useState<Mentor | null>(null);
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        setLoading(true);
        console.log("Fetching mentors from API...");
        const response = await mentorshipApi.getMentors({ limit: 20 });
        console.log("Mentors API response:", response);
        if (response.success) {
          console.log("Setting mentors:", response.data);
          setMentors(response.data);
        } else {
          console.error("API returned error:", response);
        }
      } catch (err: any) {
        console.error("Failed to fetch mentors:", err);
        showError("Failed to load mentors");
      } finally {
        setLoading(false);
      }
    };
    fetchMentors();
  }, []);

  // Map API mentor to local mentor structure
  const mappedMentors: Mentor[] = mentors.map((m: any) => ({
    _id: m._id,
    user: m.user?._id || m.user || "",
    firstName: m.firstName || "",
    lastName: m.lastName || "",
    name: `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.name || "Mentor",
    title: m.specialty || m.title || "Mentor",
    role: m.specialty || "Mentor",
    company: m.company || "",
    companyColor: m.companyColor || "bg-blue-500",
    bio: m.bio || "",
    expertise: m.expertise || [],
    pricePerSession: m.pricePerSession || 0,
    rating: m.rating || 0,
    reviewsCount: m.reviewsCount || 0,
    totalSessions: m.sessionsCount || m.totalSessions || 0,
    totalStudents: m.studentsCount || m.totalStudents || 0,
    studentsCount: m.studentsCount || 0,
    sessionsCount: m.sessionsCount || 0,
    reviews: m.reviewsCount || 0,
    students: m.studentsCount || 0,
    sessions: m.sessionsCount || 0,
    price: m.pricePerSession || 0,
    isVerified: m.verified || m.isVerified || false,
    isTopMentor: m.topMentor || m.isTopMentor || false,
    verified: m.verified || m.isVerified || false,
    topMentor: m.topMentor || m.isTopMentor || false,
    avatar: m.avatar || m.user?.avatar || "",
    image: m.avatar || m.user?.avatar || "",
    linkedin: m.linkedin || "",
    twitter: m.twitter || "",
    website: m.website || "",
    responseTime: m.responseTime || "< 24 hrs",
    availability: "Available",
    languages: m.languages || [],
  }));

  // Map API mentor to local mentor structure or use MENTORS as fallback
  const displayMentors = mappedMentors.length > 0 ? mappedMentors : MENTORS;

  const filtered = displayMentors.filter((m) => {
    const q = searchQuery.toLowerCase();
    return (
      ((m.name || "").toLowerCase().includes(q) ||
        (m.expertise || []).some((e: string) => e.toLowerCase().includes(q)) ||
        (m.company || "").toLowerCase().includes(q)) &&
      (selectedExpertise === "All" ||
        (m.expertise || []).some((e: string) => e.includes(selectedExpertise)))
    );
  }).sort((a, b) => {
    switch (sortBy) {
      case "rating":
        return b.rating - a.rating;
      case "price-lo":
        return a.price - b.price;
      case "price-hi":
        return b.price - a.price;
      case "sessions":
        return b.sessions - a.sessions;
      default:
        return b.students - a.students;
    }
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".hero-word",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.07,
          ease: "expo.out",
          delay: 0.1,
        },
      );
      gsap.fromTo(
        ".stat-card",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.08,
          ease: "expo.out",
          delay: 0.25,
        },
      );
      gsap.fromTo(
        ".benefit-card",
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.45,
          stagger: 0.07,
          ease: "expo.out",
          scrollTrigger: { trigger: ".benefits-grid", start: "top 85%" },
        },
      );
      gsap.fromTo(
        ".step-card",
        { opacity: 0, x: -20 },
        {
          opacity: 1,
          x: 0,
          duration: 0.5,
          stagger: 0.1,
          ease: "expo.out",
          scrollTrigger: { trigger: ".steps-grid", start: "top 85%" },
        },
      );
      gsap.fromTo(
        ".mentor-card",
        { opacity: 0, y: 28 },
        {
          opacity: 1,
          y: 0,
          duration: 0.45,
          stagger: 0.05,
          ease: "expo.out",
          scrollTrigger: { trigger: ".mentors-grid", start: "top 85%" },
        },
      );
    });
    return () => ctx.revert();
  }, [filtered]);

  const BENEFITS = [
    {
      icon: Video,
      title: "1-on-1 Video Calls",
      desc: "Personalized sessions tailored to your exact goals and pace — no group noise.",
    },
    {
      icon: MessageCircle,
      title: "Direct Messaging",
      desc: "Stay connected between sessions. Ask questions anytime, get answers fast.",
    },
    {
      icon: Calendar,
      title: "Flexible Scheduling",
      desc: "Book sessions that genuinely fit your timezone and weekly routine.",
    },
    {
      icon: Shield,
      title: "Verified Experts",
      desc: "Every mentor is manually vetted — credentials, experience, and teaching quality.",
    },
    {
      icon: Award,
      title: "Career Guidance",
      desc: "Personalized roadmaps to accelerate your path to your next role or promotion.",
    },
    {
      icon: Zap,
      title: "Instant Results",
      desc: "Most students report measurable progress after their first 2 sessions.",
    },
  ];

  const HOW_IT_WORKS = [
    {
      step: "01",
      title: "Browse & Match",
      desc: "Filter by expertise, company background, availability, and budget to find your ideal mentor.",
    },
    {
      step: "02",
      title: "Book a Session",
      desc: "Choose a slot that fits your schedule and confirm instantly — no back-and-forth emails.",
    },
    {
      step: "03",
      title: "Meet & Grow",
      desc: "1-on-1 video call, personalized action plan, direct message access. Growth starts day one.",
    },
  ];

  return (
    <>
      <SEO
        title="1-on-1 Expert Mentorship - Career Guidance & Skill Development"
        description="Connect with verified mentors from Google, Microsoft, Amazon, and top companies. Get personalized career advice, technical mentorship, portfolio reviews, and interview preparation."
        keywords="expert mentorship, career coaching, tech mentor, portfolio review, interview preparation, 1-on-1 mentoring, professional development, leadership coaching"
      />
      <main className="relative min-h-screen bg-white">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative pt-20 pb-16 overflow-hidden bg-gradient-to-b from-gray-50 via-white to-gray-50 rounded-t-3xl">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(245, 158, 11, 0.15) 0%, transparent 50%),
                         radial-gradient(circle at 75% 75%, rgba(245, 158, 11, 0.1) 0%, transparent 50%)`
          }} />
        </div>

        <div className="relative z-10 max-w-6xl px-4 mx-auto sm:px-6 lg:px-8">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/30">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs font-semibold tracking-wider uppercase text-primary">
                1-on-1 Mentorship
              </span>
            </div>
          </div>

          {/* Headline */}
          <h1 className="mb-6 text-2xl font-bold leading-tight tracking-tight text-center text-white sm:text-3xl lg:text-3xl">
            <span className="block text-gray-800">Master Your Craft with </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-400">Expert Mentors</span>
          </h1>

          {/* Subheadline */}
          <p className="max-w-2xl mx-auto mb-10 text-base text-center text-gray-400 sm:text-lg">
            Connect with industry experts from top tech companies. Get personalized guidance, 
            accelerate your career growth, and achieve your goals with 1-on-1 sessions.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a
              href="#mentors"
              className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-bold text-white bg-gradient-to-r from-primary to-orange-500 rounded-xl hover:shadow-lg hover:shadow-primary/30 transition-all"
            >
              Find a Mentor
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-white border border-gray-600 rounded-xl bg-gray-800 transition-all"
            >
              How It Works
            </a>
          </div>

          {/* Stats */}
          <div className="grid max-w-4xl grid-cols-2 gap-4 mx-auto sm:grid-cols-4">
            {[
              { value: "500+", label: "Expert Mentors" },
              { value: "4.9", label: "Average Rating" },
              { value: "50k+", label: "Sessions Completed" },
              { value: "98%", label: "Success Rate" },
            ].map((stat, i) => (
              <div key={i} className="p-4 text-center border rounded-xl bg-white/5 border-white/10">
                <p className="text-2xl font-bold sm:text-3xl text-primary">{stat.value}</p>
                <p className="mt-1 text-xs text-gray-400 sm:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Social Proof ──────────────────────────────────────────────────────────── */}
      <div className="py-8 bg-gray-900 border-gray-800 border-y">
        <div className="max-w-6xl px-4 mx-auto sm:px-6 lg:px-8">
          <p className="mb-4 text-sm text-center text-gray-500">Mentors from top companies</p>
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-60">
            {["Google", "Meta", "Apple", "Amazon", "Microsoft", "Netflix"].map((company, i) => (
              <span key={i} className="text-lg font-semibold text-gray-400">{company}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── How It Works ──────────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-16 bg-white">
        <div className="max-w-6xl px-4 mx-auto sm:px-6 lg:px-8">
          <h2 className="mb-4 text-2xl font-bold text-center text-gray-900 sm:text-3xl">How It Works</h2>
          <p className="mb-12 text-center text-gray-600">Get matched with your perfect mentor in 3 simple steps</p>
          
          <div className="grid gap-8 sm:grid-cols-3">
            {[
              { step: "01", title: "Browse Mentors", desc: "Explore our curated list of expert mentors and find your perfect match" },
              { step: "02", title: "Book a Session", desc: "Schedule a 1-on-1 video call at a time that works for you" },
              { step: "03", title: "Accelerate Growth", desc: "Get personalized guidance and achieve your career goals" },
            ].map((item, i) => (
              <div key={i} className="p-6 text-center">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 text-xl font-bold rounded-full bg-primary/10 text-primary">
                  {item.step}
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits ──────────────────────────────────────────────────────── */}
      <section className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 py-14">
        <div className="flex flex-col gap-4 mb-10 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-orange-500 bg-orange-50 border border-orange-100 px-2.5 py-1 rounded-full">
              Why Mentorship
            </span>
            <h2 className="mt-3 text-xl font-black text-gray-900 sm:text-2xl">
              Everything you need
              <br />
              to level up — fast.
            </h2>
          </div>
          <p className="max-w-xs text-xs leading-relaxed text-gray-500">
            Structured, personalized, results-oriented. Not another course.
            Actual human guidance.
          </p>
        </div>

        <div className="grid gap-4 benefits-grid sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b, i) => (
            <div
              key={i}
              className="benefit-card group bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="flex items-center justify-center mb-4 transition-colors duration-300 bg-gray-100 w-9 h-9 rounded-xl group-hover:bg-gray-900">
                <b.icon className="w-4 h-4 text-gray-600 transition-colors duration-300 group-hover:text-white" />
              </div>
              <h3 className="text-xs font-bold text-gray-900 mb-1.5">
                {b.title}
              </h3>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                {b.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────────── */}
      <section
        id="how-it-works"
        className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 pb-14"
      >
        <div className="bg-[#0a0a0a] rounded-3xl p-7 lg:p-12 overflow-hidden relative">
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #fff 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500 rounded-full blur-[120px] opacity-10 pointer-events-none" />

          <div className="relative z-10">
            <div className="flex flex-col justify-between gap-6 mb-10 lg:flex-row lg:items-end">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-orange-400 mb-3 block">
                  The Process
                </span>
                <h2 className="text-2xl font-black leading-tight text-white lg:text-3xl">
                  3 steps to get
                  <br />
                  personalized guidance
                </h2>
              </div>
              <p className="max-w-xs text-xs leading-relaxed text-white/30">
                No complex onboarding. Find your mentor and start learning
                within minutes.
              </p>
            </div>

            <div className="relative grid gap-6 steps-grid md:grid-cols-3">
              <div
                className="hidden md:block absolute top-7 left-[calc(33%+2rem)] right-[calc(33%+2rem)] h-px"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(249,115,22,0.5), rgba(249,115,22,0.1) 50%, rgba(249,115,22,0.5))",
                }}
              />
              {HOW_IT_WORKS.map((s, i) => (
                <div key={i} className="step-card">
                  <div className="flex flex-col items-center justify-center mb-4 border w-14 h-14 rounded-2xl bg-white/5 border-white/8">
                    <span className="text-[9px] text-orange-400/60 font-bold tracking-widest uppercase">
                      Step
                    </span>
                    <span className="text-lg font-black leading-none text-orange-400">
                      {s.step}
                    </span>
                  </div>
                  <h3 className="mb-2 text-sm font-bold text-white">
                    {s.title}
                  </h3>
                  <p className="text-xs leading-relaxed text-white/40">
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Mentors Section ───────────────────────────────────────────────── */}
      <section
        id="mentors"
        className="px-4 pb-16 mx-auto max-w-7xl sm:px-6 lg:px-8"
      >
        <div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full">
              Our Mentors
            </span>
            <h2 className="mt-3 text-xl font-black text-gray-900 sm:text-2xl">
              Find your perfect match
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Every mentor is manually vetted — we check credentials,
              communication, and teaching quality.
            </p>
          </div>
          <p className="text-xs text-gray-500 shrink-0">
            Showing{" "}
            <span className="font-bold text-gray-900">{filtered.length}</span>{" "}
            mentors
          </p>
        </div>

        {/* Search & filter bar */}
        <div className="z-10 p-3 mb-5 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <div className="flex flex-col sm:flex-row gap-2.5">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, skill, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-9 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all placeholder:text-gray-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute text-gray-400 -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="relative">
              <select
                value={selectedExpertise}
                onChange={(e) => setExpert(e.target.value)}
                className="appearance-none w-full sm:w-44 pl-3 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-blue-400 cursor-pointer"
              >
                {EXPERTISE_AREAS.map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
            </div>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none w-full sm:w-44 pl-3 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-blue-400 cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="flex items-center gap-1.5 mt-2.5 overflow-x-auto pb-0.5 no-scrollbar">
            {EXPERTISE_AREAS.slice(0, 8).map((a) => (
              <button
                key={a}
                onClick={() => setExpert(a)}
                className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all
                  ${selectedExpertise === a ? "bg-gray-900 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-400"}`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid gap-5 mentors-grid md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((m) => (
              <MentorCard
                key={m._id || m.id}
                mentor={m}
                onSelect={() => setSelected(m)}
              />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center bg-white border border-gray-200 border-dashed rounded-2xl">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-2xl">
              <Search className="w-5 h-5 text-gray-400" />
            </div>
            <h3 className="mb-1 text-sm font-bold text-gray-900">
              No mentors found
            </h3>
            <p className="mb-4 text-xs text-gray-500">
              Try adjusting your search or clearing the filter
            </p>
            <button
              onClick={() => {
                setSearchQuery("");
                setExpert("All");
              }}
              className="px-4 py-2 text-xs font-semibold text-white transition-colors bg-gray-900 rounded-xl hover:bg-gray-800"
            >
              Reset filters
            </button>
          </div>
        )}
      </section>

      {/* ── CTA Banner ────────────────────────────────────────────────────── */}
      <section className="px-4 pb-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="relative rounded-3xl overflow-hidden bg-[#0a0a0a] p-8 lg:p-14">
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #fff 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-48 bg-orange-500 rounded-full blur-[100px] opacity-15 pointer-events-none" />
          <div className="relative z-10 flex flex-col items-center justify-between gap-8 lg:flex-row">
            <div>
              <h2 className="mb-2 text-2xl font-black leading-tight text-white lg:text-3xl">
                Ready to accelerate
                <br />
                your career?
              </h2>
              <p className="max-w-md text-sm leading-relaxed text-white/40">
                Join 48,000+ professionals who transformed their careers through
                personalized 1-on-1 mentorship.
              </p>
              <div className="flex flex-wrap gap-4 mt-5 text-[11px] text-white/30">
                {[
                  "No long-term commitment",
                  "Cancel anytime",
                  "Money-back guarantee",
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <CheckCircle className="w-3 h-3 text-emerald-500" />
                    {t}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row shrink-0">
              <a
                href="#mentors"
                className="inline-flex items-center gap-2 px-6 py-3 text-xs font-bold text-gray-900 transition-colors bg-white shadow-lg rounded-xl hover:bg-gray-100 shadow-black/30 group"
              >
                Find a Mentor{" "}
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-6 py-3 text-xs font-semibold text-white transition-colors border bg-white/6 border-white/10 rounded-xl hover:bg-white/10"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mentor Modal ──────────────────────────────────────────────────── */}
      {selectedMentor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-lg w-full max-h-[92vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal image — also clean, no heavy overlay */}
            <div
              className="relative overflow-hidden bg-gray-100 rounded-t-3xl shrink-0"
              style={{ height: "260px" }}
            >
              <img
                src={selectedMentor.image}
                alt={selectedMentor.name}
                className="object-cover object-top w-full h-full"
              />

              {/* Very subtle bottom fade just for legibility of text below */}
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/40 to-transparent" />

              <button
                onClick={() => setSelected(null)}
                className="absolute top-3.5 right-3.5 w-8 h-8 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/60 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>

              <div className="absolute top-3.5 left-3.5 flex gap-1.5">
                <span
                  className={`text-[9px] font-black uppercase tracking-widest text-white px-2 py-0.5 rounded-md shadow-md ${selectedMentor.companyColor}`}
                >
                  {selectedMentor.company}
                </span>
                {selectedMentor.topMentor && (
                  <span className="text-[9px] font-bold text-amber-900 bg-amber-400 px-2 py-0.5 rounded-md flex items-center gap-0.5 shadow-md">
                    <Star className="w-2.5 h-2.5 fill-amber-900" /> Top Mentor
                  </span>
                )}
              </div>

              {/* Name pinned to bottom-left over the subtle fade */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-end justify-between">
                  <div>
                    <h2 className="text-lg font-black text-white drop-shadow-md">
                      {selectedMentor.name}
                    </h2>
                    <p className="text-white/80 text-xs mt-0.5 drop-shadow-sm">
                      {selectedMentor.role} · {selectedMentor.company}
                    </p>
                  </div>
                  {selectedMentor.verified && (
                    <div className="flex items-center justify-center bg-white rounded-full shadow-md w-7 h-7">
                      <BadgeCheck className="w-4 h-4 text-blue-500" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal body */}
            <div className="p-5">
              <div className="flex flex-wrap items-center gap-3 pb-4 mb-4 border-b border-gray-100">
                <div className="flex items-center gap-1.5">
                  <RatingStars rating={selectedMentor.rating || 0} size="md" />
                  <span className="text-xs font-bold text-gray-900">
                    {selectedMentor.rating?.toFixed(1) || "New"}
                  </span>
                  <span className="text-gray-400 text-[10px]">
                    ({selectedMentor.reviewsCount || selectedMentor.reviews || 0} reviews)
                  </span>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                  <Users className="w-3 h-3" />
                  {selectedMentor.studentsCount || selectedMentor.students || 0} students
                </div>
                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                  <Clock className="w-3 h-3" />
                  Responds {selectedMentor.responseTime}
                </div>
                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                  <Globe className="w-3 h-3" />
                  {selectedMentor.languages.join(", ")}
                </div>
              </div>

              {/* Social Links in Modal */}
              {(selectedMentor.linkedin || selectedMentor.twitter || selectedMentor.website) && (
                <div className="flex gap-2 mb-4">
                  {selectedMentor.linkedin && (
                    <a href={selectedMentor.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 transition hover:text-blue-600">
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {selectedMentor.twitter && (
                    <a href={selectedMentor.twitter} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 transition hover:text-sky-500">
                      <Twitter className="w-4 h-4" />
                    </a>
                  )}
                  {selectedMentor.website && (
                    <a href={selectedMentor.website} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 transition hover:text-gray-600">
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                </div>
              )}

              <p className="mb-5 text-xs leading-relaxed text-gray-600">
                {selectedMentor.bio}
              </p>

              <div className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                  Areas of Expertise
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedMentor.expertise.map((s) => (
                    <span
                      key={s}
                      className="text-[11px] font-semibold bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                  Availability
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                  {selectedMentor.availability}
                </div>
              </div>

              <div className="p-4 mb-5 border border-gray-100 bg-gray-50 rounded-2xl">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">
                  What's Included
                </p>
                <div className="space-y-2">
                  {[
                    "1-on-1 Video Call (60 min)",
                    "Direct messaging access",
                    "Personalized action plan",
                    "Recording of the session",
                    "Follow-up resources & links",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-xs text-gray-700"
                    >
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div>
                  <p className="text-lg font-black text-gray-900">
                    {fmtXAF(selectedMentor.pricePerSession || selectedMentor.price)}
                  </p>
                  <p className="text-[9px] text-gray-400 uppercase tracking-wide">
                    per session · No hidden fees
                  </p>
                </div>
                <button 
                  onClick={() => navigate(`/mentorship/${selectedMentor._id}`)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-blue-600 text-white text-xs font-bold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20 group"
                >
                  View Profile{" "}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </main>
    </>
  );
};

export default MentorshipPage;
